/**
 * Чтение рекламного конфига, приехавшего из БД в `window.initial.context.adConfig`.
 *
 * Форма: `{slot: {variant: [payload, ...]}}`. Плечи ротации уже выбраны на сервере,
 * поэтому здесь ничего не разыгрывается — что пришло, то и рендерим.
 *
 * Если конфига нет (кампания ещё не перенесена или выключена флагом `is_active`),
 * резолв возвращает null, и вызывающий код остаётся на конфиге из бандла —
 * с той семантикой фолбэка, которая у его шаблона сейчас.
 */

import { pickVariant } from "./adVariant";

// Типы яндексовых блоков, привязанных к вьюпорту: контейнер им не нужен.
const NO_CONTAINER_TYPES = new Set(["fullscreen", "overlay", "floorAd"]);

export function getAdConfig() {
    if (typeof window === "undefined" || !window.initial || !window.initial.context) {
        return null;
    }

    return window.initial.context.adConfig || null;
}

// Учёт источника: откуда фактически взялся каждый слот — из БД или из бандла.
// Слоты спрашивают поштучно во время рендера, поэтому решения копятся и уходят
// в консоль одной строкой на следующем тике: иначе на страницу пришлось бы
// два десятка сообщений.
const slotSource = {};
let reportScheduled = false;
// Сколько слотов вошло в последний отчёт: отличать «ничего не изменилось» от
// «появились новые слоты» нужно, чтобы поздний рендер не остался без строки.
let reportedSlots = -1;

function noteSource(slot, fromDb) {
    if (slot in slotSource) {
        return;
    }

    slotSource[slot] = fromDb;
    scheduleSourceReport();
}

function scheduleSourceReport() {
    if (reportScheduled || typeof window === "undefined" || typeof console === "undefined") {
        return;
    }

    reportScheduled = true;

    setTimeout(() => {
        reportScheduled = false;

        const slots = Object.keys(slotSource).sort();

        if (slots.length === reportedSlots) {
            return;
        }

        reportedSlots = slots.length;

        const fromDb = slots.filter((slot) => slotSource[slot]);
        const fromBundle = slots.filter((slot) => !slotSource[slot]);

        console.info(
            "[ad-config] кампания: %s | из БД: %s | из бандла: %s",
            getAdConfig() ? "есть в БД" : "в БД не заведена",
            fromDb.join(", ") || "—",
            fromBundle.join(", ") || "—",
        );
    }, 0);
}

// Отчёт печатается и тогда, когда конфиг не спросил ни один слот. Без этого лог
// молчал на превью (`?fpr=...` без `view=full`): ir-блоки там не рендерятся, спрашивать
// конфиг некому — и молчание читалось как «лог сломан», хотя верный ответ другой:
// «эту страницу конфиг из БД не касается».
//
// Задержка, а не `load` или ближайший тик: порядок «модуль — рендер — load» зависит от
// того, как подключён бандл, и на нулевой задержке пустой отчёт обгонял слоты — строк
// становилось две. Слот, спросивший конфиг, печатает строку сам, а этот отчёт к своему
// сроку увидит, что список не изменился, и промолчит.
const INITIAL_REPORT_DELAY = 1500;

if (typeof window !== "undefined") {
    setTimeout(scheduleSourceReport, INITIAL_REPORT_DELAY);
}

/** Есть ли слот в конфиге из БД (для любого варианта). */
export function hasSlot(slot, adConfig = getAdConfig()) {
    const fromDb = Boolean(adConfig && adConfig[slot]);

    noteSource(slot, fromDb);

    return fromDb;
}

/**
 * Плейсменты слота для текущего варианта.
 *
 * Ось варианта одна на все слоты — full/prev × pc/mob. Слоты, заведённые в бандле
 * только по устройству, раскрыты на все четыре ещё на этапе переноса.
 *
 * Три разных исхода: null — слота в кампании нет, вызывающий код идёт в бандл;
 * пустой массив — слот есть, но это плечо ротации проиграло, рисовать нечего;
 * непустой — рисуем то, что пришло.
 */
export function resolveSlot(slot, { isFull, isDesktop, adConfig = getAdConfig() } = {}) {
    if (!adConfig || !adConfig[slot]) {
        noteSource(slot, false);

        return null;
    }

    const all = adConfig[slot][pickVariant(isFull, isDesktop)];

    if (!all) {
        // Слот в кампании есть, но не для этого варианта: рисует его всё равно бандл.
        noteSource(slot, false);

        return null;
    }

    // Пустой список — плечо ротации проиграло: слот в кампании заведён, показывать
    // нечего. Возвращаем именно пустой массив, а не null: null означает «слота в БД
    // нет» и уводит вызывающий код в конфиг из бандла, где нарисовалось бы второе
    // плечо той же группы — и на странице оказались бы оба сразу.
    if (!all.length) {
        noteSource(slot, true);

        return [];
    }

    // provider=json — это не блок, а id ленты, из которой место заполняется
    // тизерами. Рендерят её ToPad/Feed/InRead по своим срезам, не AdSlot.
    const entries = all.filter((entry) => entry.provider !== "json");

    // Только provider=json — рисовать AdSlot нечего, место заполнит лента из бандла.
    noteSource(slot, entries.length > 0);

    return entries.length ? entries : null;
}

/**
 * id ленты `fetchJsonAds` для текущего варианта.
 *
 * Лежит на местах, которые лента заполняет (provider='json'), поэтому берём первый
 * попавшийся — значение у всех одно.
 */
export function resolveJsonObjectId({ isFull, isDesktop, fallback = null, adConfig = getAdConfig() } = {}) {
    if (!adConfig) {
        noteSource("json(objectId)", false);

        return fallback;
    }

    const variant = pickVariant(isFull, isDesktop);

    for (const byVariant of Object.values(adConfig)) {
        for (const entry of byVariant[variant] || []) {
            if (entry.provider === "json" && entry.blockId) {
                noteSource("json(objectId)", true);

                return entry.blockId;
            }
        }
    }

    noteSource("json(objectId)", false);

    return fallback;
}

/**
 * Слот проиграл ротацию: в кампании он заведён, но показывать на нём нечего.
 *
 * Отличать обязательно: если уйти отсюда в конфиг из бандла, на странице окажется
 * второе плечо той же группы — то самое, которое сервер и не выбрал.
 */
function isSilenced(entries) {
    return Array.isArray(entries) && entries.length === 0;
}

/** Первый плейсмент слота или null — и для незаведённого слота, и для проигравшего плеча. */
export function resolveSlotEntry(slot, options) {
    const entries = resolveSlot(slot, options);

    return entries && entries.length ? entries[0] : null;
}

/**
 * Значение слота, который передаётся пропом, а не рендерится компонентом:
 * `refresh` (слаг страницы), `backclick`/`popup` (smi placement-id), `sidebar`
 * (яндексовый блок внутри колонки).
 *
 * Второй способ чтения конфига после `AdSlot`, и больше их нет. Разделение
 * проходит по тому, чем слот является в шаблоне: если слот — это весь блок,
 * его рисует `AdSlot`; если слот — значение внутри чужого блока, оно читается
 * отсюда. Третьего не нужно: `field` покрывает слоты, у которых значение лежит
 * не в `blockId` (у `refresh` это `pageUrl` — слаг, а не id информера).
 *
 * Возвращает `fallback`, если кампании нет в БД, — вызывающий код остаётся
 * на значении из бандла.
 */
export function resolveSlotValue(
    slot,
    { isFull, isDesktop, fallback = null, field = "blockId", adConfig = getAdConfig() } = {},
) {
    const entries = resolveSlot(slot, { isFull, isDesktop, adConfig });

    if (isSilenced(entries)) {
        noteSource(slot, true);

        return null;
    }

    const entry = entries && entries.length ? entries[0] : null;
    const value = entry ? entry[field] : null;
    // Запись без нужного поля равносильна её отсутствию: у `refresh` бывает только
    // `blockId` информера, а слага нет — тогда редирект должен остаться бандловым.
    const fromDb = value !== null && value !== undefined;

    noteSource(slot, fromDb);

    return fromDb ? value : fallback;
}

// Настройки поведения слота приезжают в каждом его плейсменте: отдельной ветки
// в конфиге под них нет, чтобы не менять форму `{слот: {вариант: [блоки]}}`.
const SETTING_KEYS = ["maxWidgets", "yandexOnly", "isSticky"];

/**
 * Все значения слота списком — для пропов, принимающих несколько блоков сразу
 * (колонка виджетов в сайдбаре 2013: в бандле там массив из четырёх id).
 *
 * Отдельная функция, а не флаг у `resolveSlotValue`: слот с четырьмя записями и
 * слот с одной — разные вещи для вызывающего кода, и молчаливое приведение одного
 * к другому уже стоило трёх виджетов из четырёх.
 */
export function resolveSlotValues(
    slot,
    { isFull, isDesktop, fallback = null, field = "blockId", adConfig = getAdConfig() } = {},
) {
    const entries = resolveSlot(slot, { isFull, isDesktop, adConfig });

    if (isSilenced(entries)) {
        noteSource(slot, true);

        return [];
    }

    const values = (entries || [])
        .map((entry) => entry[field])
        .filter((value) => value !== null && value !== undefined);

    noteSource(slot, values.length > 0);

    return values.length ? values : fallback;
}

/**
 * Настройки поведения слота из БД либо null, если кампании нет или настройки не заданы.
 *
 * Возвращает только то, что действительно настроено: незаполненное поле в БД
 * означает «взять из бандла», и вызывающий код сам решает, чем подставиться.
 */
export function resolveSlotSettings(slot, { isFull, isDesktop, adConfig = getAdConfig() } = {}) {
    const entry = resolveSlotEntry(slot, { isFull, isDesktop, adConfig });

    if (!entry) {
        return null;
    }

    const settings = {};

    for (const key of SETTING_KEYS) {
        if (entry[key] !== undefined) {
            settings[key] = entry[key];
        }
    }

    return Object.keys(settings).length ? settings : null;
}

/** Нужен ли яндексовому блоку свой контейнер. */
export function needsContainer(entry) {
    return !NO_CONTAINER_TYPES.has(entry.type);
}

/** id контейнера: явный renderTo, иначе конвенция `yandex_rtb_<blockId>`. */
export function containerId(entry) {
    return entry.renderTo || `yandex_rtb_${entry.blockId}`;
}
